<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="InertiaLink"
                :pages="[
                    {
                        name: $tc('messages.intervention_records', 2),
                        href: meta.links.index,
                    },
                    {
                        name: group.name,
                        href: meta.links.index_group,
                    },
                    {
                        name: $t('messages.student'),
                        href: meta.links.index_student,
                    },
                ]"
            />
        </template>

        <div class="md:grid md:grid-cols-3 md:gap-6">
            <div class="mt-5 md:mt-0 bg-gray-50">
                <AppWell>
                    <AppCard>
                        <InertiaLink :href="meta.links.student_show">
                            <img
                                :src="student.avatar_or_default"
                                class="mx-auto rounded-full h-32 w-32 border-4 border-white"
                            />
                        </InertiaLink>

                        <div class="bg-white px-4 py-5 sm:px-6">
                            <div class="flex flex-col justify-center items-center">
                                <h3 class="text-lg leading-6 font-medium text-gray-900">
                                    <InertiaLink :href="meta.links.student_show">
                                        {{ student.name_or_nickname }}
                                    </InertiaLink>
                                </h3>
                                <h3 class="leading-6 font-base text-gray-600">
                                    {{ $t('messages.student') }}
                                </h3>
                            </div>
                        </div>
                    </AppCard>
                </AppWell>
            </div>

            <div class="mt-5 md:mt-0 md:col-span-2">
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-3">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.date')" for="date" />
                            {{ interventionRecord.date_display }}
                        </div>
                    </div>

                    <div class="sm:col-span-3">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.duration')" for="duration" />
                            {{ interventionRecord.duration }}
                        </div>
                    </div>

                    <div class="sm:col-span-3">
                        <div class="mt-1">
                            <FormLabel :value="$tc('messages.external_entities', 1)" for="external_entity_id" />
                            {{ interventionRecord.external_entity }}
                        </div>
                    </div>

                    <div class="sm:col-span-3">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.channel')" for="channel_id" />
                            {{ interventionRecord.channel }}
                        </div>
                    </div>

                    <div class="sm:col-span-12">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.participants')" for="participants" />
                            <p id="participants" class="mt-1 w-full whitespace-pre-line">
                                {{ interventionRecord.participants }}
                            </p>
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.requested_by')" for="requested_by" />
                            {{ interventionRecord.requested_by }}
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.reason')" for="reason" />
                            {{ interventionRecord.reason }}
                        </div>
                    </div>

                    <div class="sm:col-span-12">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.topics_covered')" for="topics_covered" />
                            <p id="topics_covered" class="mt-1 w-full whitespace-pre-line">
                                {{ interventionRecord.topics_covered }}
                            </p>
                        </div>
                    </div>

                    <div class="sm:col-span-12">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.agreements_purposes')" for="agreements_purposes" />
                            <p id="agreements_purposes" class="mt-1 w-full whitespace-pre-line">
                                {{ interventionRecord.agreements_purposes }}
                            </p>
                        </div>
                    </div>

                    <div class="sm:col-span-8">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.next_intervention_date')" for="next_intervention_date" />
                            {{ interventionRecord.next_intervention_date_display }}
                        </div>
                    </div>

                    <div class="sm:col-span-4">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.time')" for="next_intervention_time" />
                            {{ interventionRecord.next_intervention_time }}
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <label for="type" class="block text-sm font-medium leading-5 text-gray-700">
                            {{ $t('messages.intervention_with') }}
                        </label>
                        <div class="mt-1 rounded-md shadow-sm">
                            {{ interventionRecord.type }}
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <label for="assessment_level" class="block text-sm font-medium leading-5 text-gray-700">
                            {{ $t('messages.intervention_status') }}
                        </label>
                        <div class="mt-1 rounded-md shadow-sm">
                            {{ interventionRecord.assessment_level }}
                        </div>
                    </div>

                    <div class="sm:col-span-12">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.agreements_purposes')" for="agreements_purposes" />
                            <p id="participants" class="mt-1 w-full whitespace-pre-line">
                                {{ interventionRecord.evaluation_of_intervention }}
                            </p>
                        </div>
                    </div>
                </div>

                <div class="sm:col-span-6 mt-3" v-if="interventionRecord.links.length">
                    <label class="block text-sm font-medium leading-5 text-gray-700">
                        {{ $tc('messages.links', 2) }}
                    </label>
                    <div class="mt-1">
                        <LinkListDisplay :links="interventionRecord.links" />
                    </div>
                </div>
            </div>
        </div>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormInput from '@/components/Forms/FormInput.vue';
import AppCard from '@/Shared/AppCard.vue';
import AppWell from '@/Shared/AppWell.vue';
import LinkListDisplay from '@/components/pageSpecific/LinkListDisplay.vue';

export default {
    components: {
        LinkListDisplay,
        PanelPage,
        AppWell,
        AppCard,
        FormInput,
        FormLabel,
    },
    layout: Layout,
    name: 'Show',
    props: ['meta', 'student', 'group', 'interventionRecord'],
};
</script>
